/**
 * CreatePlanModal — follows the CreateItemModal pattern.
 *
 * A plan can be created from:
 *   1. Item Pool — an existing saved pool of items
 *   2. Collection — a named container of items
 *   3. Published priorities — a ratified, published priority list
 */

import { useEffect, useRef, useState } from 'react';
import { X, ClipboardList, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { SearchableSelect } from '../../components/shared/SearchableSelect';
import {
  createPlanCycleWithInitialVersion,
  listItemPools,
  listCollectionsForPlanning,
  listPublishedPriorityLists,
  type PlanningInputOption,
} from '../../lib/planningService';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import {
  setUpPlanScenario,
  isPlanScenarioSetupComplete,
  type PlanScenarioSetupState,
} from '../../lib/planning/planScenarioSetup';

type SourceType = 'item_pool' | 'collection' | 'published_priority_list';

const SOURCES: { value: SourceType; label: string; description: string }[] = [
  {
    value: 'item_pool',
    label: 'Item Pool',
    description: 'A saved set of items assembled for planning',
  },
  {
    value: 'collection',
    label: 'Collection',
    description: 'A thematic grouping of items — items are snapshotted at plan creation',
  },
  {
    value: 'published_priority_list',
    label: 'Published priorities',
    description: 'A ratified, published priority list to plan against',
  },
];

interface CreatePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called once the plan cycle exists. `scenarioReady` is true only when the
   * engine scenario + scope binding + activation (BREAK 2) all completed —
   * the caller uses it to decide whether to land on the engine view with a
   * confirmation or the plain plan detail page.
   */
  onCreated: (planCycleId: string, opts?: { scenarioReady: boolean; scopeName?: string }) => void;
  /** Prefill from planning handoff query params (cycle report → New Plan). */
  initialSource?: SourceType;
  initialInputId?: string;
  initialName?: string;
}

const SETUP_STEP_LABELS: Record<string, string> = {
  scenario: 'Create the planning scenario',
  scope_binding: 'Set the engine scope to this list',
  activate_scenario: 'Make it the active scenario',
};

export function CreatePlanModal({
  isOpen,
  onClose,
  onCreated,
  initialSource,
  initialInputId,
  initialName,
}: CreatePlanModalProps) {
  const userId = useUserStore((s) => s.userId);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);

  const [name, setName] = useState('');
  const [source, setSource] = useState<SourceType>('item_pool');
  const [selectedInputId, setSelectedInputId] = useState('');
  const [options, setOptions] = useState<PlanningInputOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /** Bumps when the modal opens so option load waits for handoff seed. */
  const [openEpoch, setOpenEpoch] = useState(0);
  const preferInputIdRef = useRef<string | undefined>(undefined);

  // BREAK 2: the plan cycle this modal created, kept around so a scenario
  // setup failure never loses it — "Finish setup" retries against the same
  // cycle instead of creating a second one.
  const [createdPlan, setCreatedPlan] = useState<{ id: string; name: string } | null>(null);
  const [setupState, setSetupState] = useState<PlanScenarioSetupState | null>(null);
  const [settingUp, setSettingUp] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);

  // Seed fields once per open from handoff query params
  useEffect(() => {
    if (!isOpen) return;
    setName(initialName ?? '');
    setSource(initialSource ?? 'item_pool');
    preferInputIdRef.current = initialInputId;
    setSelectedInputId(initialInputId ?? '');
    setError('');
    setCreatedPlan(null);
    setSetupState(null);
    setOpenEpoch((n) => n + 1);
    setTimeout(() => nameRef.current?.focus(), 50);
  }, [isOpen, initialSource, initialInputId, initialName]);

  // Load options whenever source changes while open (after seed epoch)
  useEffect(() => {
    if (!isOpen || openEpoch === 0) return;
    setOptions([]);
    setLoadingOptions(true);
    const preferId = preferInputIdRef.current;

    const loaders: Record<SourceType, () => Promise<{ data: PlanningInputOption[]; error: string | null }>> = {
      item_pool: listItemPools,
      collection: listCollectionsForPlanning,
      published_priority_list: listPublishedPriorityLists,
    };

    void loaders[source]().then(({ data }) => {
      setOptions(data);
      setLoadingOptions(false);
      if (preferId && data.some((o) => o.id === preferId)) {
        setSelectedInputId(preferId);
        preferInputIdRef.current = undefined;
      } else if (!preferId) {
        setSelectedInputId('');
      }
    });
  }, [source, isOpen, openEpoch]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const canSave = name.trim() && selectedInputId;
  const selectedInputName = options.find((o) => o.id === selectedInputId)?.name ?? name.trim();

  // BREAK 2: after the plan cycle exists, wire the planning engine to it —
  // scenario bound to the cycle, scope set to the source list, activated —
  // so the engine is ready to run instead of leaving the user to do this by
  // hand in ScenarioList / the scope picker. `resume` is set on a "Finish
  // setup" retry so completed steps aren't repeated.
  const runSetup = async (planCycleId: string, resume?: PlanScenarioSetupState | null) => {
    if (!workspaceId) {
      setSetupState({
        scenarioId: resume?.scenarioId ?? null,
        scopeBound: resume?.scopeBound ?? false,
        activated: resume?.activated ?? false,
        steps: [{ step: 'scenario', ok: false, error: 'No workspace found.' }],
      });
      return null;
    }
    const state = await setUpPlanScenario(
      {
        planCycleId,
        planName: name.trim(),
        workspaceId,
        sourceType: source,
        inputId: selectedInputId,
        inputName: selectedInputName,
      },
      resume ?? undefined,
    );
    setSetupState(state);
    return state;
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const { data, error: err } = await createPlanCycleWithInitialVersion({
        name: name.trim(),
        input_type: source,
        input_reference_id: selectedInputId,
        created_by: userId ?? undefined,
      });
      if (err || !data) {
        setError(err ?? 'Failed to create plan');
        return;
      }
      const plan = { id: data.planCycle.id, name: data.planCycle.name };
      setCreatedPlan(plan);
      setSaving(false);
      setSettingUp(true);
      const state = await runSetup(plan.id);
      setSettingUp(false);
      if (state && isPlanScenarioSetupComplete(state)) {
        onCreated(plan.id, { scenarioReady: true, scopeName: selectedInputName });
        onClose();
      }
      // Incomplete setup: stay open with the plan already created — the
      // "Finish setup" / "Skip for now" footer below takes it from here.
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  const handleFinishSetup = async () => {
    if (!createdPlan) return;
    setSettingUp(true);
    const state = await runSetup(createdPlan.id, setupState);
    setSettingUp(false);
    if (state && isPlanScenarioSetupComplete(state)) {
      onCreated(createdPlan.id, { scenarioReady: true, scopeName: selectedInputName });
      onClose();
    }
  };

  const handleSkipSetup = () => {
    if (!createdPlan) return;
    onCreated(createdPlan.id, { scenarioReady: false });
    onClose();
  };

  // Newest decision first, so a list ratified or published moments ago is the
  // first thing the user sees when the dropdown opens (F10/F15).
  const orderedOptions = [...options].sort((a, b) => {
    const at = a.decidedAt ? Date.parse(a.decidedAt) : 0;
    const bt = b.decidedAt ? Date.parse(b.decidedAt) : 0;
    return bt - at;
  });

  const selectOptions = orderedOptions.map((o) => ({
    value: o.id,
    // F15: a ratified list is plannable but is not yet the Plan of Record —
    // say which, rather than presenting both as "published".
    label: [
      o.name,
      o.itemCount ? `(${o.itemCount} items)` : '',
      o.state === 'ratified' ? '· Ratified' : o.state === 'published' ? '· Published' : '',
    ]
      .filter(Boolean)
      .join(' '),
  }));

  const sourcePlaceholder =
    source === 'item_pool'              ? 'Search item pools…' :
    source === 'collection'             ? 'Search collections…' :
                                          'Search priority lists…';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-[var(--bg-raised)] shadow-2xl shadow-gray-300/40 border border-[var(--rule)]/80">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-7 h-7 bg-[var(--slate-dim)]">
              <ClipboardList className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--text-pri)]">New Plan</h2>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">Choose a starting point for this plan</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* BREAK 2: plan created but engine setup didn't finish — keep the plan,
            show exactly what's left, offer a one-click retry. Replaces the
            form entirely so the user isn't tempted to "create" a second plan. */}
        {createdPlan && setupState && !isPlanScenarioSetupComplete(setupState) ? (
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-start gap-2.5 border border-[var(--green)] bg-[var(--green-dim)] px-3 py-2.5">
              <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                Plan "{createdPlan.name}" was created. The planning engine isn't fully set up yet —
                finish these steps for a ready-to-run scenario.
              </p>
            </div>
            <ul className="space-y-2">
              {(['scenario', 'scope_binding', 'activate_scenario'] as const).map((stepName) => {
                const done =
                  stepName === 'scenario' ? !!setupState.scenarioId :
                  stepName === 'scope_binding' ? setupState.scopeBound :
                  setupState.activated;
                const failedStep = setupState.steps.find((s) => s.step === stepName && !s.ok);
                return (
                  <li key={stepName} className="flex items-start gap-2.5 text-sm">
                    {done ? (
                      <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <p className={done ? 'text-[var(--text-pri)]' : 'text-[var(--coral)] font-medium'}>
                        {SETUP_STEP_LABELS[stepName]}
                      </p>
                      {failedStep?.error && (
                        <p className="text-xs text-[var(--coral)] mt-0.5">{failedStep.error}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
        <div className="px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              Plan name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              placeholder="e.g. Q3 2026 Delivery Plan"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)] transition-all"
            />
          </div>

          {/* Source */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-2">
              Build from <span className="text-[var(--coral)]">*</span>
            </label>
            <div className="space-y-2">
              {SOURCES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => {
                    preferInputIdRef.current = undefined;
                    setSource(s.value);
                  }}
                  className={`w-full text-left px-4 py-3  border transition-all ${
                    source === s.value
                      ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-1 ring-indigo-300'
                      : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)]'
                  }`}
                >
                  <p className={`text-sm font-semibold ${source === s.value ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                    {s.label}
                  </p>
                  <p className={`text-xs mt-0.5 ${source === s.value ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                    {s.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Input selector */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              {source === 'item_pool'              ? 'Item Pool' :
               source === 'collection'             ? 'Collection' :
                                                     'Priority List'}{' '}
              <span className="text-[var(--coral)]">*</span>
            </label>
            {loadingOptions ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-ter)]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading…
              </div>
            ) : options.length === 0 ? (
              <p className="text-xs text-[var(--text-ter)] italic">
                No {source === 'item_pool' ? 'item pools' : source === 'collection' ? 'collections' : 'ratified or published priority lists'} found in this workspace.
              </p>
            ) : (
              <>
                <SearchableSelect
                  options={selectOptions}
                  value={selectedInputId}
                  onChange={setSelectedInputId}
                  // F10: the closed trigger used to show only the current
                  // selection, so an empty-looking control read as "there is
                  // nothing to pick". Say how many there are without opening it.
                  placeholder={`${sourcePlaceholder} (${selectOptions.length} available)`}
                  className="w-full"
                />
                <p className="mt-1.5 text-[11px] text-[var(--text-ter)]">
                  {selectOptions.length} available · newest first
                </p>
              </>
            )}
          </div>

          {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
        </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--rule)] bg-[var(--surface)]/60">
          {createdPlan && setupState && !isPlanScenarioSetupComplete(setupState) ? (
            <>
              <button
                type="button"
                onClick={handleSkipSetup}
                className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
              >
                Skip for now
              </button>
              <button
                type="button"
                onClick={() => void handleFinishSetup()}
                disabled={settingUp}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {settingUp && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Finish setup
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={!canSave || saving || settingUp}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {(saving || settingUp) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Create Plan
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
